import type {
  AuthenticationMethodDescription,
  Credential,
  IntegrationDescription,
  AutomationsGetCredentialResponse,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import * as R from "remeda";
import { v4 as uuid } from "uuid";
import { CaptionBlock } from "../../../../components/CaptionBlock";
import type { AutomationPlatformOption } from "../../../../utils/constants";
import * as S from "./styled";
import { getIntegrationLogo } from "../../../../utils/integration-logo";
import { useRootStore } from "../../../../stores/hooks";
import { getLocalCssColorVariables } from "../../../../utils/cssVariablesUtils";
import {
  INPUT_ACTION_NAME_ID,
  Integrations,
} from "../../../../utils/constants";
import SvgLogoGoogle from "../../../../../generated/images/LogoGoogle";
import { useEngineClient } from "../../../../services/engineClient";
import { translateIfPresent } from "../../../../locales/i18n";
import {
  Icon,
  KeyValueCustomSelect,
  Paragraph,
  Tooltip,
  useSelectState,
} from "@smartsuite/react-ui/lib";
import { SolutionColors } from "@smartsuite/types/lib/constants/color";
import { TextColorNames } from "@smartsuite/types";
import { isCredentialIdEmpty } from "./no-creds";
import { DisplayState, UsedAutomations } from "./used-automations";
import {
  findInputAndPick,
  pickValueConfigString,
} from "../../../../utils/inputs";

export const CredentialSelector: React.FC<{
  integrationId?: string;
  integrations: IntegrationDescription[];
  credentialId?: string;
  credentials: Credential[];
  onChange: (id: string) => void;
  onAdd: (authMethod: AuthenticationMethodDescription) => void;
  onRemove: (id: string) => void;
  isSubmitted: boolean;
  loading: boolean;
  isAction?: boolean;
  /**
   * Authentication method ids declared by the action/trigger this selector is
   * rendered for. When provided, only those authentication methods are offered
   * (an integration may register several, e.g. Slack's OAuth + SmartSuite ones).
   */
  authenticationMethodIds?: string[];
}> = ({
  integrationId,
  integrations,
  credentialId,
  credentials,
  onChange,
  onAdd,
  onRemove,
  isSubmitted,
  loading,
  isAction,
  authenticationMethodIds,
}) => {
  const store = useRootStore();
  const [options, setOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [authMethods, setAuthMethods] = useState<
    Array<{
      option: AutomationPlatformOption;
      value: AuthenticationMethodDescription;
    }>
  >([]);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [integration, setIntegration] = useState<IntegrationDescription>();
  const [usedAutomationsModalState, setUsedAutomationsModalState] =
    useState<DisplayState>(DisplayState.CLOSED);
  const [selectState, setSelectState] = useSelectState();
  const engineClient = useEngineClient();

  const authenticationMethodIdsKey = (authenticationMethodIds ?? []).join(",");

  const applicableAuthMethods = useMemo(
    () => getApplicableAuthMethods(integration, authenticationMethodIds),
    [integration, authenticationMethodIdsKey],
  );

  const label = useMemo(() => {
    if (applicableAuthMethods[0]?.label !== undefined) {
      return applicableAuthMethods[0].label;
    }
    return `${integration?.label ?? ""} ${t("automation-form.account")}`;
  }, [integration, applicableAuthMethods]);

  useEffect(() => {
    const integration = integrations.find(
      (integration) => integration.integrationId === integrationId,
    );
    if (integration !== undefined) {
      setIntegration(integration);
    }
  }, [integrationId, integrations]);

  useEffect(() => {
    store.modal.modal.closeOnClickOutside =
      usedAutomationsModalState === DisplayState.CLOSED;
  }, [usedAutomationsModalState]);

  useEffect(() => {
    (async () => {
      if (integration === undefined) {
        return;
      }
      const applicableAuthMethodIds = applicableAuthMethods.map(
        (authMethod) => authMethod.authenticationMethodId,
      );
      const credentialList = credentials.filter(
        (c) =>
          c.authenticationMethodReference?.integrationId === integrationId &&
          (applicableAuthMethodIds.length > 0
            ? applicableAuthMethodIds.includes(
                c.authenticationMethodReference?.authenticationMethodId ?? "",
              )
            : true),
      );

      setOptions(credentialList.map(createCredentialOption));

      if (!isCredentialIdEmpty(credentialId)) {
        const selectedCred = credentialList.find(
          (cred) => cred.credentialId === credentialId,
        );

        if (selectedCred === undefined) {
          // Try to get credential by id
          if (integrationId !== undefined) {
            let cred: AutomationsGetCredentialResponse | undefined;
            if (credentialId === undefined) {
              return;
            }
            try {
              cred = await engineClient.getCredentialById({
                integrationId,
                credentialId,
              });
            } catch (e) {
              setSelectedOption("");
            }
            if (cred?.credential) {
              credentialList.push(cred.credential);
              setSelectedOption(cred.credential.credentialId);
              setOptions(credentialList.map(createCredentialOption));
            }
          }
        } else {
          setSelectedOption(createCredentialOption(selectedCred).value);
        }
      }

      setAuthMethods(
        applicableAuthMethods.map((authMethod) => ({
          option: createAuthMethodOption(integration?.label),
          value: authMethod,
        })),
      );
    })().catch((err) => {
      console.error(err);
    });
  }, [
    integrationId,
    integration,
    credentialId,
    credentials,
    authenticationMethodIdsKey,
  ]);

  const removeCredential = (credentialId: string): void => {
    const usedConnections: Array<{ link: string; automationId: string }> = [];
    store.automations.list.forEach((automation) => {
      if (
        automation.automationId ===
        store.automations.candidate.self.automationId
      ) {
        return;
      }
      const actions = store.automations.candidate
        .automationAllActions(automation)
        .filter((action) => action.credentialId === credentialId);
      actions.forEach((action) => {
        let actionName =
          findInputAndPick(
            action.inputs,
            INPUT_ACTION_NAME_ID,
            pickValueConfigString,
          ) ?? "";
        if (actionName === "") {
          const integration = store.integrations.list.find(
            (integration) =>
              integration.integrationId ===
              action.actionReference?.integrationId,
          );
          if (integration !== undefined) {
            const integrationAction = integration.actions.find(
              (item) => item.actionId === action.actionReference?.actionId,
            );
            if (integrationAction !== undefined) {
              actionName = integrationAction.label;
            }
          }
        }
        usedConnections.push({
          link: `${automation.label} • ${actionName}`,
          automationId: automation.automationId,
        });
      });
    });

    const description =
      usedConnections.length === 0
        ? t("confirm-modal.no-other-connections")
        : `${t("confirm-modal.delete-connection-all")}</p>`;

    store.modal.openConfirmModal({
      type: "warning",
      title: t("confirm-modal.are-you-sure"),
      description,
      closeOnClickOutside: usedAutomationsModalState === DisplayState.CLOSED,
      body: (
        <S.Center>
          <UsedAutomations
            usedConnections={usedConnections}
            onChangeState={setUsedAutomationsModalState}
          ></UsedAutomations>
        </S.Center>
      ),
      positiveButtonText: t("confirm-modal.yes-delete-connection"),
      negativeButtonText: t("confirm-modal.no-cancel"),
      onClose: () => {
        store.modal.closeConfirmModal();
      },
      onConfirm: () => {
        onRemove(credentialId);
        store.modal.closeConfirmModal();
      },
    });
  };

  const renderOption = (
    option: { value: string; label: string },
    active: boolean,
  ): React.ReactNode => {
    const isAddAuthOption = R.isTruthy(
      authMethods.find((m) => m.option.value === option.value),
    );
    const IntegrationLogo = integrationId
      ? getIntegrationLogo(integrationId)
      : false;
    return isAddAuthOption ? (
      <S.AddNewItem>
        <S.PlusIconWrap>
          <Icon name={"plus"} color={TextColorNames.primary} />
        </S.PlusIconWrap>
        {option.label}
      </S.AddNewItem>
    ) : (
      <S.SelectItem className={active ? "active" : ""}>
        {IntegrationLogo && <IntegrationLogo style={{ marginRight: 10 }} />}
        {option.label}
        <S.IconsWrap>
          {active && (
            <Icon
              size={"M"}
              name={"checkmark"}
              color={SolutionColors.basePrimaryGreen}
              className={"checkmark"}
            />
          )}
          {
            <Tooltip
              className={"trash-tooltip"}
              text={t("automation-form.remove-account")}
              placements={["bottom"]}
              target={
                <Icon
                  size={"S"}
                  name={"trash-duotone"}
                  color={SolutionColors.basePrimaryGrey}
                  onClick={() => {
                    removeCredential(option.value);
                  }}
                />
              }
            />
          }
        </S.IconsWrap>
      </S.SelectItem>
    );
  };

  const renderFooter = (): React.ReactNode => {
    return (
      <S.FooterWrap>
        {authMethods.map((m) => (
          <S.AddNewItem key={m.option.value} onClick={() => onAdd(m.value)}>
            <S.PlusIconWrap>
              <Icon name={"plus"} color={TextColorNames.primary} />
            </S.PlusIconWrap>
            {m.option.label}
          </S.AddNewItem>
        ))}
      </S.FooterWrap>
    );
  };

  const Logo = getIntegrationLogo(integrationId ?? "");

  const renderSelected = (option: {
    value: string;
    label: string;
  }): React.ReactNode => {
    return (
      <S.SelectedItem>
        {integrationId?.startsWith(Integrations.google) ? (
          <SvgLogoGoogle />
        ) : (
          <Logo />
        )}
        {option.label}
      </S.SelectedItem>
    );
  };

  return (
    <S.SelectFieldContainer>
      <CaptionBlock
        description={{
          icon: "",
          label,
          subLabel: applicableAuthMethods[0]?.helpText ?? "",
          display: {
            showRequiredStatus: 1,
            display: { oneofKind: "always", always: 0 },
          },
          dynamic: !loading,
        }}
      />

      <KeyValueCustomSelect
        className={
          (isSubmitted && !selectedOption) ||
          store.automations.candidate.triggerPermissionsError !== undefined
            ? "is-error"
            : ""
        }
        value={selectedOption}
        placeholder={
          selectState === "active"
            ? t("automation-form.credential-selector-new")
            : "\u00a0"
        }
        placeholderArrowStyle="filled-dark"
        portalStyles={getLocalCssColorVariables(store.theme)}
        showClear={false}
        options={options}
        state={selectState}
        renderSelectedOption={renderSelected}
        renderOption={renderOption}
        renderDropdownFooter={renderFooter}
        onOpenedChange={setSelectState}
        onChange={(value) => {
          if (value !== credentialId) {
            const option = options.find((option) => option.value === value);
            if (option !== undefined) {
              setSelectedOption(String(value));
              onChange(String(value));
            }
          }
        }}
      />
      {!isAction &&
        store.automations.candidate.triggerPermissionsError !== undefined && (
          <Paragraph color="error" size="xs">
            {translateIfPresent(
              `caption.${store.automations.candidate.triggerPermissionsError}`,
              store.automations.candidate.triggerPermissionsError,
            )}
          </Paragraph>
        )}

      {isSubmitted && !selectedOption ? (
        <Paragraph color="error" size="xs">
          {t("automation-form.this-field-is-required")}
        </Paragraph>
      ) : null}
    </S.SelectFieldContainer>
  );
};

/**
 * An integration can register more than one authentication method (Slack, for
 * example, registers an OAuth method for "Send Message" and a SmartSuite one
 * for the actionable-notification action). Each method renders its own
 * "+ Add new <integration> credential" entry, so without this filter the
 * dropdown shows the same option several times. When the caller does not
 * declare an applicable method, every method remains available.
 */
export const getApplicableAuthMethods = (
  integration: IntegrationDescription | undefined,
  authenticationMethodIds: string[] | undefined,
): AuthenticationMethodDescription[] => {
  const methods = integration?.authenticationMethods ?? [];
  if (
    authenticationMethodIds === undefined ||
    authenticationMethodIds.length === 0
  ) {
    return methods;
  }
  const applicable = methods.filter((method) =>
    authenticationMethodIds.includes(method.authenticationMethodId),
  );
  return applicable;
};

const createCredentialOption = (
  credential: Credential,
): AutomationPlatformOption => ({
  value: credential.credentialId,
  label: credential.label,
});

const createAuthMethodOption = (
  type: string = "",
): AutomationPlatformOption => ({
  value: uuid(),
  label: t("automation-form.credential-selector-add-option", { type }),
});
